/**
 * Google Ads integration helper.
 *
 * Two products, both env-gated, both NO-OPS when their env var is empty:
 *  - Google AdSense  → REACT_APP_GOOGLE_ADSENSE_CLIENT   (e.g. "ca-pub-XXXXXXXXXXXXXXXX")
 *  - Google Ads conversions (gtag.js) → REACT_APP_GOOGLE_ADS_CONVERSION_ID  (e.g. "AW-1234567890")
 *
 * Call `mountGoogleAds()` once at app boot.
 * Call `trackAdsConversion('signup')` after a signup/subscription success.
 */
const ADSENSE_CLIENT = process.env.REACT_APP_GOOGLE_ADSENSE_CLIENT || "";
const ADS_CONVERSION_ID = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID || "";
const ADS_CONVERSION_LABELS = {
  signup: process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL || "",
};

let mounted = false;

function loadScript(src, attrs = {}) {
  if (typeof document === "undefined") return;
  if (document.querySelector(`script[data-karm-src="${src}"]`)) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  s.dataset.karmSrc = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
}

export function mountGoogleAds() {
  if (mounted) return;
  mounted = true;
  // 1) AdSense (passive — ads only render where <AdSlot /> is mounted).
  // The script tag itself now also lives statically in public/index.html
  // (required for Google's site-verification crawler, which doesn't run JS).
  // Skip re-injecting it here if it's already on the page.
  if (ADSENSE_CLIENT && !document.querySelector('script[src*="adsbygoogle.js"]')) {
    loadScript(`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`, {
      crossorigin: "anonymous",
    });
  }
  // 2) Google Ads / gtag.js (for conversion tracking & remarketing)
  if (ADS_CONVERSION_ID) {
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${ADS_CONVERSION_ID}`);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", ADS_CONVERSION_ID);
  }
}

export function trackAdsConversion(event = "signup", payload = {}) {
  if (!ADS_CONVERSION_ID) return;
  const label = ADS_CONVERSION_LABELS[event] || "";
  if (typeof window.gtag !== "function") return;
  if (label) {
    window.gtag("event", "conversion", {
      send_to: `${ADS_CONVERSION_ID}/${label}`,
      ...payload,
    });
  } else {
    // Generic event — useful for remarketing audiences even without a labeled conversion
    window.gtag("event", event, payload);
  }
}

export const adsConfig = {
  adsenseClient: ADSENSE_CLIENT,
  adsConversionId: ADS_CONVERSION_ID,
  enabled: !!(ADSENSE_CLIENT || ADS_CONVERSION_ID),
};
